import { WaardelijstenApi } from "../api/waardelijsten";
import { Teksten } from "../content";
import { DropdownInfoFactory, WaardelijstFactory } from "../factories";
import { OrgMembershipsFactory } from "../factories/orgMemberships";
import { IdentifierWithChildren } from "../factories/waardelijst";
import { Identifier } from "../models";
import { DocumentMetadata } from "../models/document-metadata";
import { Roles } from "../models/Roles";
import { IListValidator } from "../models/validator";
import { AppDispatch } from "../store";
import {
  FormState,
  markFileRemoved,
  updateAggregatieKenmerk,
  updateAltTitel,
  updateCreatieDatum,
  updateDocumentSoorten,
  updateExtraMetadata,
  updateFile,
  updateGeldigheidBegindatum,
  updateGeldigheidEinddatum,
  updateIdentifiers,
  updateInformatiecategorieen,
  updateMedeverantwoordelijken,
  updateOmschrijving,
  updateOnderwerpen,
  updateOnderwerpenRonl,
  updateOpsteller,
  updatePublicatiebestemming,
  updatePublisher,
  updateTaal,
  updateThemas,
  updateTitel,
  updateVerantwoordelijke,
  updateVerkorteTitel,
  updateWeblocatie,
} from "../store/reducers/document-metadata";
import { ContainerAtom, SpacerAtom } from "../ui/atoms";
import { ICheckbox } from "../ui/interfaces/Checkbox";
import { FormConfig } from "../ui/interfaces/FormConfig";
import {
  LabelMolecule,
  NaamWaardeInputfieldMolecule,
  RadioInputMolecule,
  SuggestieMolecule,
  TextInputMolecule,
} from "../ui/molecules";
import { createValidators } from "../validations/create-validators";
import { formatDate, stringToDate } from "./DateFormatter";
import { UseFormContextStatesDocument } from "./DocumentFormContext";

interface Props {
  dispatch: AppDispatch;
  disableForm: boolean;
  fcd: ReturnType<typeof UseFormContextStatesDocument>;
  requiredRefs: HTMLInputElement[];
  mode: "update" | "create";
  factories: {
    waardelijsten: WaardelijstenApi;
    waardelijstFactory: WaardelijstFactory;
    orgMemberships: OrgMembershipsFactory;
    dropdownInfo: DropdownInfoFactory;
  };
  data: DocumentMetadata;
  formState: FormState;
  fileName?: string;
  fileSelected?: string | File;
  originalWeblocatieUrl?: string;
  ksmOrganisaties: IdentifierWithChildren[];
  ronlOnderwerpen: IdentifierWithChildren[];
  formStateIndex: number;
  hoofdDoc: FormState;
  authState: any;
  uploadInfoElement: () => JSX.Element | null;
  documenthandelingenElement: (validation?: IListValidator[]) => JSX.Element;
  conceptToggleElement: () => JSX.Element;
  uitgesteldPublicerenToggleElement: () => JSX.Element;
}

export function CreateFormConfig(props: Props): FormConfig {
  const { document } = props.data;

  const {
    requiredDropdownValidations,
    requiredDropdownValidationsRONL,
    requiredTextInputValidations,
    textInputValidations,
    fileInputValidations,
    weblocatieValidations,
  } = createValidators();

  const pushRequiredRef = (ref: any) => props.requiredRefs.push(ref);

  const makeFileField = (): any => ({
    type: "file",
    id: "file",
    selectedFileName: props.fileName,
    disabled: props.disableForm,
    validations: fileInputValidations,
    fileUploadBlocked: props.mode === "update" && !!props.originalWeblocatieUrl,
    onSelectFile: (file: File | undefined) => {
      const opentPopup = !!file && !!document.pid;
      if (opentPopup) props.fcd.snapshotVoorWijziging(props.formState);

      props.dispatch(updateFile(file));

      if (opentPopup) props.fcd.setPopupUpdate(true);
    },
    inputRef: pushRequiredRef,
    weblocatieInputRef: pushRequiredRef,
    selectedInput: props.fileSelected,
    required: true,
    tooltip: Teksten.createScreen.document.tooltipFile,
    weblocatie: document.weblocatie,
    weblocatieTooltip: Teksten.createScreen.document.tooltipWeblocatie,
    weblocatieValidations: weblocatieValidations,
    onWeblocatieChange: (url: string | undefined) => {
      const isChanged = url !== document.weblocatie;
      const opentPopup = isChanged && !!url && !!document.pid && url !== props.originalWeblocatieUrl;
      if (opentPopup) props.fcd.snapshotVoorWijziging(props.formState);

      props.dispatch(updateWeblocatie(url));

      if (opentPopup) props.fcd.setPopupUpdate(true);
    },
    onRemoveFile: () => {
      props.dispatch(markFileRemoved());
    },
    onWeblocatieHerindexatie:
      props.mode === "update" && props.originalWeblocatieUrl && document.weblocatie === props.originalWeblocatieUrl
        ? () => {
            props.fcd.snapshotVoorWijziging(props.formState);
            props.dispatch(updateWeblocatie(props.originalWeblocatieUrl));
            props.fcd.setPopupHerindexeren(true);
          }
        : undefined,
  });

  const makePublisherDropdown = (): any => ({
    type: "dropdown",
    id: Teksten.createScreen.publisher.id,
    disabled: props.disableForm || props.mode === "update",
    tekst: Teksten.createScreen.publisher,
    onUpdated: (identifier: Identifier) => props.dispatch(updatePublisher(identifier)),
    inputRef: pushRequiredRef,
    selected: [document.publisher],
    categories: props.factories.dropdownInfo.generateCheckboxListSelect(
      props.fcd.actievePublishers,
      document.publisher
    ),
    validations: requiredDropdownValidations,
    singleSelect: true,
    required: true,
  });

  const makeDocumentRelatieGroup = (): any => ({
    type: "group",
    layout: "row",
    id: Teksten.createScreen.documentrelatie.label + "_label",
    label: Teksten.createScreen.documentrelatie.label,
    tooltip: Teksten.createScreen.documentrelatie.tooltip,
    fields: [
      {
        type: "text",
        id: Teksten.createScreen.documentrelatie.id_veld1,
        value: props.formStateIndex === 0 ? "HoofdDocument" : "Bijlage bij",
        disabled: true,
      },
      {
        type: "text",
        id: Teksten.createScreen.documentrelatie.id_veld2,
        value: props.formStateIndex === 0 ? "" : props.hoofdDoc?.meta.document.titelcollectie.officieleTitel,
        disabled: true,
      },
    ],
  });

  const makeUploadInfo = (): any => ({
    type: "custom",
    id: "uploadInfo",
    render: props.uploadInfoElement,
  });

  const makeTitelField = (): any => ({
    type: "text",
    label: Teksten.createScreen.titel.label,
    id: Teksten.createScreen.titel.id,
    placeholder: Teksten.createScreen.titel.placeholder,
    tooltip: Teksten.createScreen.titel.tooltip,
    value: document.titelcollectie.officieleTitel,
    disabled: props.disableForm,
    validations: requiredTextInputValidations,
    onChange: (titel: string) => props.dispatch(updateTitel(titel)),
    inputRef: pushRequiredRef,
    required: true,
  });

  const makeInformatieCategorieDropdown = (): any => {
    const filtered = props.fcd.informatiecategorieen.filter(
      (iwc: IdentifierWithChildren) =>
        iwc.id !== "https://identifier.overheid.nl/tooi/def/thes/kern/wooInformatiecategorieen" &&
        iwc.id !== "https://identifier.overheid.nl/tooi/def/thes/kern/c_139c6280" &&
        iwc.id !== "https://identifier.overheid.nl/tooi/def/thes/kern/c_aab6bfc7" &&
        iwc.id !== "https://identifier.overheid.nl/tooi/def/thes/kern/c_40a05794" &&
        iwc.id !== "https://identifier.overheid.nl/tooi/def/thes/kern/c_89ee6784" &&
        iwc.id !== "https://identifier.overheid.nl/tooi/def/thes/kern/c_c76862ab"
    );

    return {
      type: "dropdown",
      id: Teksten.createScreen.informatiecategorieen.id,
      disabled: props.disableForm,
      tekst: Teksten.createScreen.informatiecategorieen,
      onUpdated: (identifier: Identifier) => props.dispatch(updateInformatiecategorieen(identifier)),
      inputRef: pushRequiredRef,
      selected: document.classificatiecollectie.informatiecategorieen,
      categories: props.factories.dropdownInfo.generateCheckboxListSelect(
        filtered,
        document.classificatiecollectie.informatiecategorieen
      ),
      validations: requiredDropdownValidations,
      singleSelect: true,
      required: true,
    };
  };

  const makeDocumentSoortDropdown = (): any => {
    const docsoortenSource =
      document.publicatiebestemming !== undefined && document.publicatiebestemming.length > 0
        ? props.factories.waardelijstFactory.createDocumentsoortenLijstKST(
            props.authState.roles.some((ds: string) => ds === Roles.KST)
          )
        : props.factories.waardelijstFactory.createDocumentsoortenLijst();

    return {
      type: "dropdown",
      id: Teksten.createScreen.documentsoort.id,
      disabled: props.disableForm,
      tekst: Teksten.createScreen.documentsoort,
      onUpdated: (identifier: Identifier) => props.dispatch(updateDocumentSoorten(identifier)),
      inputRef: pushRequiredRef,
      selected: document.classificatiecollectie.documentsoorten ?? [],
      categories: props.factories.dropdownInfo.generateCategorlyListSelectFromList(
        docsoortenSource,
        document.classificatiecollectie.documentsoorten ?? []
      ),
      validations:
        document.publicatiebestemming !== undefined && document.publicatiebestemming.length > 0
          ? requiredDropdownValidationsRONL
          : undefined,
      singleSelect: true,
      required: document.publicatiebestemming !== undefined && document.publicatiebestemming.length > 0,
      validateOpties: true,
    };
  };

  const makeIdentifierField = (): any => {
    const bijlagePrefillEnabled = process.env.REACT_APP_BIJLAGE_PREFILL === "true";
    const hoofdDocIdentifier = props.hoofdDoc?.meta.document.identifiers[0];
    const isBijlage = props.formStateIndex !== 0;
    const huidigeWaarde = document.identifiers[0] ?? "";
    const toonSuggestie =
      bijlagePrefillEnabled && isBijlage && !!hoofdDocIdentifier && hoofdDocIdentifier !== huidigeWaarde;

    return {
      type: "custom",
      id: Teksten.createScreen.identifier.id,
      render: () => (
        <>
          <TextInputMolecule
            label={Teksten.createScreen.identifier.label}
            id={Teksten.createScreen.identifier.id}
            placeholder={Teksten.createScreen.identifier.placeholder}
            tooltip={Teksten.createScreen.identifier.tooltip}
            value={huidigeWaarde}
            disabled={props.disableForm}
            validations={requiredTextInputValidations}
            onChange={(value: string) => props.dispatch(updateIdentifiers([value]))}
            inputRef={pushRequiredRef}
            required
          />
          {toonSuggestie && (
            <SuggestieMolecule
              id={Teksten.createScreen.identifier.id + "_suggestie"}
              disabled={props.disableForm}
              suggesties={[{ label: "Hoofddocument", waarde: hoofdDocIdentifier }]}
              onApply={(waarde: string) => props.dispatch(updateIdentifiers([waarde]))}
            />
          )}
        </>
      ),
    };
  };

  const makeExtraMetadata = (): any => ({
    type: "custom",
    id: "extraMetadata",
    render: props.documenthandelingenElement,
  });

  const makeTaalDropdown = (): any => ({
    type: "dropdown",
    id: Teksten.createScreen.taal.id,
    tekst: Teksten.createScreen.taal,
    disabled: props.disableForm,
    onUpdated: (identifier: Identifier) => props.dispatch(updateTaal(identifier)),
    inputRef: pushRequiredRef,
    selected: [document.language],
    categories: props.factories.dropdownInfo.generateCheckboxListSelect(
      props.fcd.taal,
      document.language,
      (checkbox: ICheckbox) => checkbox.label !== "" && checkbox.label !== "taal"
    ),
    validations: requiredDropdownValidations,
    singleSelect: true,
    required: true,
  });

  const makeConceptToggle = (): any => ({
    type: "custom",
    id: Teksten.createScreen.concept.id,
    render: props.conceptToggleElement,
  });

  const makeUitgesteldToggle = (): any => ({
    type: "custom",
    id: Teksten.createScreen.uitgesteld.id,
    render: props.uitgesteldPublicerenToggleElement,
  });

  // ---------------------------------------------------- optioneel

  const makeVerantwoordelijkeDropdown = (): any => ({
    type: "dropdown",
    id: Teksten.createScreen.verantwoordelijke.id,
    disabled: props.disableForm,
    tekst: Teksten.createScreen.verantwoordelijke,
    categories: props.factories.dropdownInfo.generateCheckboxListSelect(
      props.ksmOrganisaties,
      document.verantwoordelijke
    ),
    selected: document.verantwoordelijke ? [document.verantwoordelijke] : [],
    singleSelect: true,
    onUpdated: (id: Identifier) => props.dispatch(updateVerantwoordelijke(id)),
  });

  const makeMedeverantwoordelijkenDropdown = (): any => ({
    type: "dropdown",
    id: Teksten.createScreen.medeverantwoordelijken.id,
    disabled: props.disableForm,
    tekst: Teksten.createScreen.medeverantwoordelijken,
    categories: props.factories.dropdownInfo.generateCheckboxListSelect(
      props.ksmOrganisaties,
      document.medeverantwoordelijken
    ),
    selected: document.medeverantwoordelijken ?? [],
    singleSelect: false,
    onUpdated: (id: Identifier) => props.dispatch(updateMedeverantwoordelijken(id)),
  });

  const makeOnderwerpenText = (): any => ({
    type: "text",
    id: Teksten.createScreen.onderwerpen.id,
    disabled: props.disableForm,
    label: Teksten.createScreen.onderwerpen.label,
    placeholder: Teksten.createScreen.onderwerpen.placeholder,
    value: document.onderwerpen?.[0] ?? "",
    onChange: (val: string) => props.dispatch(updateOnderwerpen([val])),
    tooltip: Teksten.createScreen.onderwerpen.tooltip,
    validations: textInputValidations,
  });

  const makeOnderwerpenRonl = (): any => ({
    type: "dropdown",
    id: Teksten.createScreen.onderwerpenRonl.id,
    disabled: props.disableForm || !document.publicatiebestemming?.includes("rijksoverheid.nl"),
    tekst: Teksten.createScreen.onderwerpenRonl,
    categories: props.factories.dropdownInfo.generateCheckboxListSelect(
      props.ronlOnderwerpen,
      document.classificatiecollectie.onderwerpenRonl
    ),
    selected: document.classificatiecollectie.onderwerpenRonl ?? [],
    singleSelect: false,
    onUpdated: (id: Identifier) => props.dispatch(updateOnderwerpenRonl(id)),
  });

  const makeThemaDropdown = (): any => ({
    type: "dropdown",
    id: Teksten.createScreen.thema.id,
    disabled: props.disableForm,
    tekst: Teksten.createScreen.thema,
    categories: props.factories.dropdownInfo.generateCategorlyListSelectFromList(
      props.factories.waardelijstFactory.createThemaLijst(),
      document.classificatiecollectie.themas ?? []
    ),
    selected: document.classificatiecollectie.themas ?? [],
    singleSelect: false,
    onUpdated: (id: Identifier) => props.dispatch(updateThemas(id)),
  });

  const makeOmschrijvingTextarea = (): any => ({
    type: "textArea",
    id: Teksten.createScreen.omschrijving.id,
    disabled: props.disableForm,
    label: Teksten.createScreen.omschrijving.label,
    placeholder: Teksten.createScreen.omschrijving.placeholder,
    value: document.omschrijvingen?.[0] ?? "",
    onChange: (val: string) => props.dispatch(updateOmschrijving(val)),
    tooltip: Teksten.createScreen.omschrijving.tooltip,
    validations: textInputValidations,
  });

  const makeVerkorteTitelsText = (): any => ({
    type: "text",
    id: Teksten.createScreen.verkorteTitels.id,
    disabled: props.disableForm,
    label: Teksten.createScreen.verkorteTitels.label,
    placeholder: Teksten.createScreen.verkorteTitels.placeholder,
    value: document.titelcollectie.verkorteTitels?.at(0),
    onChange: (val: string) => props.dispatch(updateVerkorteTitel(val)),
    tooltip: Teksten.createScreen.verkorteTitels.tooltip,
    validations: textInputValidations,
  });

  const makeAlternatieveTitelsText = (): any => ({
    type: "text",
    id: Teksten.createScreen.alternatieveTitels.id,
    disabled: props.disableForm,
    label: Teksten.createScreen.alternatieveTitels.label,
    placeholder: Teksten.createScreen.alternatieveTitels.placeholder,
    value: document.titelcollectie.alternatieveTitels?.at(0),
    onChange: (val: string) => props.dispatch(updateAltTitel(val)),
    tooltip: Teksten.createScreen.alternatieveTitels.tooltip,
    validations: textInputValidations,
  });

  const makeNaamOpstellerText = (): any => ({
    type: "text",
    id: Teksten.createScreen.naamOpsteller.id,
    disabled: props.disableForm,
    label: Teksten.createScreen.naamOpsteller.label,
    placeholder: Teksten.createScreen.naamOpsteller.placeholder,
    value: document.naamOpsteller,
    onChange: (val: string) => props.dispatch(updateOpsteller(val)),
    tooltip: Teksten.createScreen.naamOpsteller.tooltip,
    validations: textInputValidations,
  });

  const makeCreatiedatumText = (): any => ({
    type: "text",
    id: Teksten.createScreen.creatiedatum.id,
    disabled: props.disableForm,
    label: Teksten.createScreen.creatiedatum.label,
    placeholder: Teksten.createScreen.creatiedatum.placeholder,
    value: document.creatiedatum,
    onChange: (val: string) => props.dispatch(updateCreatieDatum(val)),
    tooltip: Teksten.createScreen.creatiedatum.tooltip,
    validations: textInputValidations,
  });

  const makeGeldigheidGroup = (): any => ({
    type: "group",
    id: "geldigheid",
    layout: "row",
    label: Teksten.createScreen.geldigheid.label,
    tooltip: Teksten.createScreen.geldigheid.tooltip,
    fields: [
      {
        type: "date",
        id: Teksten.createScreen.begindatum.id,
        disabled: props.disableForm,
        placeholder: Teksten.createScreen.begindatum.placeholder,
        value: document.geldigheid?.begindatum ? formatDate(stringToDate(document.geldigheid.begindatum)) : undefined,
        onChange: (datum: string) => {
          if (datum.length === 0) {
            props.dispatch(updateGeldigheidBegindatum(undefined));
            return;
          }
          const newFromDate = stringToDate(datum);
          props.dispatch(updateGeldigheidBegindatum(newFromDate));
        },
      },
      {
        type: "date",
        id: Teksten.createScreen.einddatum.id,
        disabled: props.disableForm,
        placeholder: Teksten.createScreen.einddatum.placeholder,
        value: document.geldigheid?.einddatum ? formatDate(stringToDate(document.geldigheid.einddatum)) : undefined,
        onChange: (datum: string) => {
          if (datum.length === 0) {
            props.dispatch(updateGeldigheidEinddatum(undefined));
            return;
          }
          const newFromDate = stringToDate(datum);
          props.dispatch(updateGeldigheidEinddatum(newFromDate));
        },
        minDate: document.geldigheid?.begindatum ? stringToDate(document.geldigheid.begindatum) : undefined,
      },
    ],
  });

  const makeAggregatiekenmerkText = (): any => ({
    type: "text",
    id: Teksten.createScreen.aggregatiekenmerk.id,
    disabled: props.disableForm,
    label: Teksten.createScreen.aggregatiekenmerk.label,
    placeholder: Teksten.createScreen.aggregatiekenmerk.placeholder,
    value: document.aggregatiekenmerk,
    onChange: (val: string) => props.dispatch(updateAggregatieKenmerk(val)),
    tooltip: Teksten.createScreen.aggregatiekenmerk.tooltip,
    validations: textInputValidations,
  });

  const required = [
    makeFileField(),
    makePublisherDropdown(),
    makeDocumentRelatieGroup(),
    makeUploadInfo(),
    makeTitelField(),
    makeInformatieCategorieDropdown(),
    makeDocumentSoortDropdown(),
    makeIdentifierField(),
    makeExtraMetadata(),
    makeTaalDropdown(),
    makeConceptToggle(),
    makeUitgesteldToggle(),
  ];

  const optional = [
    makeVerantwoordelijkeDropdown(),
    makeMedeverantwoordelijkenDropdown(),
    makeOnderwerpenText(),
    makeOnderwerpenRonl(),
    makeThemaDropdown(),
    makeOmschrijvingTextarea(),
    makeVerkorteTitelsText(),
    makeAlternatieveTitelsText(),
    makeNaamOpstellerText(),
    makeCreatiedatumText(),
    makeGeldigheidGroup(),
    makeAggregatiekenmerkText(),
  ];

  if (process.env.REACT_APP_RIJKSOVERHEID === "true") {
    required.unshift({
      type: "custom",
      id: "rijksoverheid",
      render: () => (
        <>
          <LabelMolecule
            id={Teksten.createScreen.rijksoverheid.id + "_label"}
            label={Teksten.createScreen.rijksoverheid.label}
            tooltip={Teksten.createScreen.rijksoverheid.tooltip}
            required={true}
          />
          <ContainerAtom type="flex" direction="row">
            <RadioInputMolecule
              disabled={props.formStateIndex !== 0 || props.disableForm}
              key={Teksten.createScreen.rijksoverheid.id + "_ja"}
              id={Teksten.createScreen.rijksoverheid.id + "_ja"}
              onChange={() => props.dispatch(updatePublicatiebestemming("rijksoverheid.nl"))}
              placeholder={"Ja"}
              value={
                document.publicatiebestemming !== undefined && document.publicatiebestemming.length > 0
                  ? "true"
                  : undefined
              }
            />
            <SpacerAtom space={4} type="horizontal" />
            <RadioInputMolecule
              disabled={props.formStateIndex !== 0 || props.disableForm}
              key={Teksten.createScreen.rijksoverheid.id + "_nee"}
              id={Teksten.createScreen.rijksoverheid.id + "_nee"}
              onChange={() => props.dispatch(updatePublicatiebestemming(undefined))}
              placeholder={"Nee"}
              value={
                document.publicatiebestemming === undefined || document.publicatiebestemming.length === 0
                  ? "true"
                  : undefined
              }
            />
          </ContainerAtom>
        </>
      ),
    });
  }

  if (process.env.REACT_APP_EXTRAMETADATA === "true") {
    optional.push({
      type: "custom",
      id: "extraMetadata",
      render: () => (
        <NaamWaardeInputfieldMolecule
          disabled={props.disableForm}
          value={
            document.extraMetadata?.[0]?.velden?.length ? document.extraMetadata[0].velden : [{ key: "", values: [""] }]
          }
          onChange={(velden) => props.dispatch(updateExtraMetadata({ velden }))}
        />
      ),
    });
  }

  const formConfig: FormConfig = {
    fields: {
      required,
      optional,
    },
  };

  return formConfig;
}
